import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * icon-bar — Shrine "Icon bar" at CONFIG-BY-CONFIG parity. A heading (with the shared word-HIGHLIGHT engine
 * and an optional background-PATTERN) over a row/column of ICON CARDS that flips between a responsive grid
 * and a horizontal scroll-snap SLIDER per breakpoint (reusing the shared slider engine, exactly like
 * testimonials / featured-collection). Each ICON is an inline child block (`column`): a Google Material
 * Symbols glyph typed by NAME (the Shopify model, with a `filledIcon` FILL axis), or a custom image
 * override, a title, and a richtext body.
 *
 * SHRINE PARITY MAP (every Shrine setting accounted for):
 *  - General: title + the TWO `title_highlight_1/2` engines (→ shared `headingHighlightFields`, which carry
 *    the style/color/gradient + all the bg-pill/underline/handwritten-line sub-knobs as engine defaults),
 *    heading_size (+ custom desktop/mobile px), text, and the 13-pattern background engine.
 *  - Icons: icon_layout, icon_size, and a `cardStyle` (folds Shrine's `cards_color_scheme` overlay-vs-surface).
 *  - Slider: type (slide/loop), autoplay (+speed), transparent arrows, arrows/dots color.
 *  - Desktop/Mobile: columns_desktop, slider_desktop/mobile, per_move_desktop, spacing, side padding,
 *    adaptive height, dots/arrows positions, columns_mobile, mobile preview.
 *  - Section (universal): color scheme, top/bottom padding (→ Shrine padding_top/bottom), width.
 *
 * DEFERRED — Shopify-PLATFORM-only knobs that have no meaning off Shopify (one-line reason each):
 *  - `display_id` / `visibility` — Shopify section-id + per-device hide utilities (host-chrome, not content).
 *  - `*_gradient` (heading + underline) / `custom_gradient_background` / `custom_image_background` /
 *    `custom_mobile_image_background` / `custom_cards_gradient_background` — per-heading gradient + CDN
 *    responsive-image backgrounds rely on Shopify `image_url` srcset; the pattern + scheme cover the look.
 *  - `color_scheme` / `cards_color_scheme` / `icon_color` / `arrows_color_scheme` / `dots_color_scheme`
 *    raw Shopify scheme GROUPS — we drive color from the universal `colorScheme` + theme tokens + the
 *    arrows/dots color selects (no Shopify scheme-group object off-platform).
 *  - block `icon` (Material-Symbols glyph NAME) + `filled_icon` — brought to PARITY: the block stores the
 *    Google Material Symbols name (`type: 'icon-name'`) and `filledIcon` drives the real FILL axis, rendered
 *    via the shared Material Symbols webfont (same engine as icons-with-content / the product icon blocks).
 *  - `desktop_full_page` / `desktop_padding_calc` — Shopify full-bleed/edge math tied to its grid; `width`
 *    (incl. `full`) covers the intent.
 *  - reveal/scroll entrance animations — explicitly out of scope.
 * COLOR comes only from the universal scheme + theme tokens (hex only in color-picker defaults).
 */
export const iconBarSchema = {
    type: 'icon-bar',
    name: 'Icon bar',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── General / Content ──────────────────────────────────────────────────
        {
            // Rich text (0019 heading model): Bold/Italic a word in the editor → it renders styled by the two
            // highlight engines below (Bold = highlight 1, Italic = highlight 2). Backward-compatible: an instance
            // saved as a plain string with `**word**` / `*word*` markup still renders + opens in the editor.
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            group: 'General',
            default: { format: 'html', html: '<p>Pourquoi acheter chez <strong>nous</strong></p>' },
            info: 'Bold or italicise words to apply the highlight styles below.',
        },
        // The two `title_highlight_1/2` engines (style + color + gradient + bg/underline/line sub-knobs).
        ...headingHighlightFields(1, 'General'),
        ...headingHighlightFields(2, 'General'),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom heading size (desktop)',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom heading size (mobile)',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'text',
            type: 'textarea',
            label: 'Text',
            group: 'General',
            rows: 2,
            maxLength: 300,
        },
        // ── Background pattern (shared 13-pattern engine) ──────────────────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'General',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'General',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'General',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'General',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'General',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Icons ──────────────────────────────────────────────────────────────
        {
            id: 'iconLayout',
            type: 'select',
            label: 'Icon layout',
            group: 'Icons',
            default: 'vertical',
            options: [
                { value: 'horizontal', label: 'Horizontal (icon beside text)' },
                { value: 'vertical', label: 'Vertical (icon above text)' },
            ],
        },
        {
            id: 'iconSize',
            type: 'select',
            label: 'Icon size',
            group: 'Icons',
            default: 'medium',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            id: 'cardStyle',
            type: 'select',
            label: 'Card style',
            group: 'Icons',
            default: 'none',
            info: 'Plain icons, a subtle overlay tint, or a full surface card behind each icon.',
            options: [
                { value: 'none', label: 'None (plain)' },
                { value: 'overlay', label: 'Overlay tint' },
                { value: 'surface', label: 'Surface card' },
            ],
        },
        // ── Slider ─────────────────────────────────────────────────────────────
        {
            id: 'sliderType',
            type: 'select',
            label: 'Slider behaviour',
            group: 'Slider',
            default: 'slide',
            info: 'Loop continuously wraps the slides; slide stops at the ends.',
            options: [
                { value: 'slide', label: 'Slide' },
                { value: 'loop', label: 'Loop' },
            ],
        },
        {
            id: 'autoplay',
            type: 'checkbox',
            label: 'Auto-rotate slides',
            group: 'Slider',
            default: false,
        },
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Change slides every',
            group: 'Slider',
            min: 1,
            max: 15,
            step: 0.5,
            unit: 's',
            default: 5,
            visibleWhen: { field: 'autoplay', equals: true },
        },
        {
            id: 'transparentArrows',
            type: 'checkbox',
            label: 'Transparent arrows',
            group: 'Slider',
            default: true,
        },
        {
            id: 'arrowsScheme',
            type: 'select',
            label: 'Arrows color',
            group: 'Slider',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        {
            id: 'dotsScheme',
            type: 'select',
            label: 'Dots color',
            group: 'Slider',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        // ── Desktop ────────────────────────────────────────────────────────────
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Columns on desktop',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 4,
        },
        {
            id: 'sliderDesktop',
            type: 'checkbox',
            label: 'Enable desktop slider',
            group: 'Desktop',
            default: false,
            info: 'When off, icons lay out as a static grid on desktop.',
        },
        {
            id: 'perMoveDesktop',
            type: 'range',
            label: 'Slides to scroll per move (desktop)',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 1,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Spacing between icons',
            group: 'Desktop',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 40,
        },
        {
            id: 'desktopSidePadding',
            type: 'range',
            label: 'Preview of prev & next slide (desktop)',
            group: 'Desktop',
            min: 0,
            max: 150,
            step: 5,
            unit: 'px',
            default: 0,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height (desktop)',
            group: 'Desktop',
            default: false,
            info: 'Each card sizes to its own content instead of equal-height cards.',
        },
        {
            id: 'desktopDots',
            type: 'select',
            label: 'Dots position (desktop)',
            group: 'Desktop',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopArrows',
            type: 'select',
            label: 'Arrows position (desktop)',
            group: 'Desktop',
            default: 'sides',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        // ── Mobile ─────────────────────────────────────────────────────────────
        {
            id: 'columnsMobile',
            type: 'select',
            label: 'Columns on mobile',
            group: 'Mobile',
            default: '1',
            options: [
                { value: '1', label: '1 column' },
                { value: '2', label: '2 columns' },
            ],
        },
        {
            id: 'sliderMobile',
            type: 'checkbox',
            label: 'Enable mobile slider',
            group: 'Mobile',
            default: true,
        },
        {
            id: 'mobilePreview',
            type: 'checkbox',
            label: 'Preview of prev & next slide (mobile)',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height (mobile)',
            group: 'Mobile',
            default: false,
        },
        {
            id: 'mobileDots',
            type: 'select',
            label: 'Dots position (mobile)',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileArrows',
            type: 'select',
            label: 'Arrows position (mobile)',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal: color scheme + padding + width) ─────────────────
        ...universalFields,
    ],
    // The ICON inline block (Shrine `column`): a curated inline-SVG glyph (or custom image), title + body.
    blocks: [
        {
            type: 'column',
            name: 'Icon',
            max: 12,
            fields: [
                {
                    id: 'icon',
                    type: 'icon-name',
                    label: 'Icon',
                    default: 'check_circle',
                    placeholder: 'check_circle',
                    info: 'Type a Google Material Symbols name (e.g. check_circle, local_shipping, verified_user). Upload a custom image below to override.',
                },
                {
                    id: 'filledIcon',
                    type: 'checkbox',
                    label: 'Filled icon',
                    default: false,
                    info: 'Renders the solid (filled) variant of the icon.',
                },
                {
                    id: 'image',
                    type: 'image',
                    label: 'Custom icon image',
                    info: 'If set, replaces the chosen glyph.',
                },
                {
                    id: 'title',
                    type: 'text',
                    label: 'Heading',
                    default: 'Votre avantage',
                    defaultKey: 'section.icon_bar_item_title',
                    // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
                    aiWritable: true,
                    maxLength: 80,
                },
                {
                    id: 'text',
                    type: 'richtext',
                    label: 'Text',
                    default: {
                        format: 'html',
                        html: '<p>Décrivez un atout qui rassure vos clients.</p>',
                    },
                },
            ],
        },
    ],
    maxBlocks: 12,
};
export const iconBarPresets = [
    {
        name: 'Icon bar',
        settings: {
            title: { format: 'html', html: '<p>Pourquoi acheter chez <strong>nous</strong></p>' },
            headingHighlight1: 'underline-solid',
            headingSize: 'h1',
            text: '@t:section.icon_bar_intro',
            iconLayout: 'vertical',
            iconSize: 'medium',
            // Shrine's default: color_scheme == cards_color_scheme → `multicolumn--same-bgs` → plain cards, no box.
            cardStyle: 'none',
            sliderType: 'slide',
            autoplay: false,
            transparentArrows: true,
            arrowsScheme: 'inverse',
            dotsScheme: 'inverse',
            columnsDesktop: 4,
            sliderDesktop: false,
            desktopSpacing: 40,
            columnsMobile: '1',
            sliderMobile: true,
            // A clean light reassurance band.
            colorScheme: 'light',
            paddingTop: 36,
            paddingBottom: 36,
            width: 'standard',
        },
        blocks: [
            {
                type: 'column',
                settings: {
                    icon: 'local_shipping',
                    title: '@t:trust.fast_shipping',
                    text: '@t:section.icon_bar_shipping_text',
                },
            },
            {
                type: 'column',
                settings: {
                    icon: 'verified_user',
                    title: '@t:checkout.pay_secure',
                    text: '@t:section.icon_bar_payment_text',
                },
            },
            {
                type: 'column',
                settings: {
                    icon: 'assignment_return',
                    title: '@t:trust.easy_returns',
                    text: '@t:section.icon_bar_returns_text',
                },
            },
            {
                type: 'column',
                settings: {
                    icon: 'support_agent',
                    title: '@t:section.icon_bar_support_title',
                    text: '@t:section.icon_bar_support_text',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const iconBarMeta = {
    label: 'Icon bar',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map